import React, { useEffect } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useAuthStore } from '@/shared/stores/authStore'
import { useSession, useTeams, useSteps, useWarRoomWS } from '../hooks'
import { useSessionStore, ROLE_PERMISSIONS } from '../stores/sessionStore'
import { useWarRoomStore } from '../stores/warRoomStore'
import { TeamHeader } from '../components/team/TeamHeader'
import { OtherTeamsSidebar } from '../components/team'
import { StepList } from '../components/step'
import { LiveFeed } from '../components/shared'
import styles from './TeamPanelPage.module.css'

export function TeamPanelPage() {
  const { sessionId, teamId } = useParams<{ sessionId: string; teamId: string }>()

  const user = useAuthStore((s) => s.user)
  const { setSessionContext, clearSessionContext } = useSessionStore()
  const recentEvents = useWarRoomStore((s) => s.recentEvents)

  const { data: session } = useSession(sessionId ?? '')
  const { data: teams } = useTeams(sessionId ?? '')
  const { data: steps } = useSteps(sessionId ?? '')

  useWarRoomWS({ sessionId: sessionId ?? '', teamId: teamId ?? '' })

  const myTeam = teams?.find((t) => t.id === teamId)
  const isTeamLead = myTeam?.lead_user_id != null && myTeam?.lead_user_id === String(user?.id ?? '')

  useEffect(() => {
    if (!sessionId || !teamId) return
    setSessionContext({
      sessionId,
      role: isTeamLead ? 'team_lead' : 'team_member',
      teamId,
      permissions: isTeamLead
        ? ROLE_PERMISSIONS.team_lead
        : ROLE_PERMISSIONS.team_member,
    })
    return () => clearSessionContext()
  }, [sessionId, teamId, isTeamLead, setSessionContext, clearSessionContext])

  if (!sessionId || !teamId) return <Navigate to="/war-room" replace />
  if (!session || !myTeam) {
    return <div className={styles.loading}>Loading…</div>
  }

  const mySteps = steps?.byTeam[teamId] ?? []
  const otherTeams = (teams ?? []).filter((t) => t.id !== teamId)

  return (
    <div className={styles.panel} data-theme="war-room-dark" data-testid="team-panel-page">
      <TeamHeader session={session} team={myTeam} steps={mySteps} />

      <div className={styles.content}>
        <main className={styles.stepArea}>
          <StepList sessionId={sessionId} teamId={teamId} steps={mySteps} />
        </main>

        <aside className={styles.sidebar}>
          <OtherTeamsSidebar
            sessionId={sessionId}
            teams={otherTeams}
            stepsByTeam={steps?.byTeam ?? {}}
          />
          <LiveFeed events={recentEvents} />
        </aside>
      </div>
    </div>
  )
}

export default TeamPanelPage
